import React, { useState, useEffect } from "react";
import { Box, Container, Fade, TextField, Typography } from "@mui/material";
import { Navigate } from "react-router-dom";

import { PhoneInput, DateInput, SecuritySection } from "../../components";
import { useAuth } from "../../contexts";
import { applyPhoneMask, formatBirthDate } from "../../utils";
import { removePhoneMask } from "../../utils/masks";
import { convertDateToISO } from "../../utils/dateUtils";
import { updateClient } from "../../services/clients";
import * as s from "./styles";
import { AssignmentInd } from "@mui/icons-material";

const Profile: React.FC = () => {
  const { client, setClient, isLogged } = useAuth();
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [birthDate, setBirthDate] = useState("");
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");

  // Carrega os dados do cliente logado quando o componente é montado
  useEffect(() => {
    if (client && client.id) {
      setName(client.name);
      // Aplica a máscara ao telefone
      setPhone(applyPhoneMask(client.phone));
      // Formata a data de nascimento
      setBirthDate(formatBirthDate(client.birthDate));
    }
  }, [client]);

  // Redireciona para a página inicial se não estiver logado
  if (!isLogged) {
    return <Navigate to="/" replace />;
  }

  const handleSave = async () => {
    if (!client || !client.id) {
      setMessage("Erro: Cliente não identificado");
      return;
    }

    // Validações básicas
    if (!name.trim()) {
      setMessage("Nome é obrigatório");
      return;
    }

    setLoading(true);
    setMessage("");

    try {
      // Prepara os dados para atualização
      const updateData: Partial<typeof client> = {
        name: name.trim(),
      };

      // Adiciona telefone se foi alterado
      if (phone.trim()) {
        const phoneWithoutMask = removePhoneMask(phone);
        updateData.phone = phoneWithoutMask;
      }

      // Adiciona data de nascimento se foi alterada
      if (birthDate.trim()) {
        const birthDateISO = convertDateToISO(birthDate);
        updateData.birthDate = birthDateISO;
      }

      // Atualiza no Firebase
      await updateClient(client.id, updateData);

      // Atualiza o contexto local com os novos dados
      const updatedClient = {
        ...client,
        ...updateData,
      };
      setClient(updatedClient);

      setMessage("Alterações salvas com sucesso!");
    } catch (error: any) {
      console.error("Erro ao salvar:", error);
      setMessage("Erro ao salvar alterações. Tente novamente.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Fade in timeout={1000}>
      <Container maxWidth="md" sx={{ py: 2, mb: 8 }}>
        <Box sx={{ mb: 2 }}>
          <Typography variant="h5" gutterBottom>
            👤 Meu perfil
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Esses dados estão visíveis para você e para nossa equipe.
          </Typography>
        </Box>
        <s.MainContainer>
          <s.SectionsContainer>
            {/* Seção Informações Pessoais */}
            <s.SectionContainer>
              <Box
                sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}
              >
                <AssignmentInd color="primary" />
                <Typography variant="subtitle1">Segurança da Conta</Typography>
              </Box>

              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Seus dados estão visíveis para você e para nossa equipe.
              </Typography>
              <s.FormContainer>
                <TextField
                  label="Nome e sobrenome"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  fullWidth
                  required
                  size="small"
                  disabled={loading}
                />
                <PhoneInput
                  label="Telefone"
                  value={phone}
                  onChange={setPhone}
                  fullWidth
                  required
                  size="small"
                  disabled={loading}
                />
                <DateInput
                  label="Data de nascimento"
                  value={birthDate}
                  onChange={setBirthDate}
                  fullWidth
                  required
                  size="small"
                  disabled={loading}
                />
              </s.FormContainer>
            </s.SectionContainer>

            {/* Seção Segurança */}
            <s.SectionContainer>
              <SecuritySection />
            </s.SectionContainer>
          </s.SectionsContainer>

          {/* Mensagem de feedback */}
          {message && (
            <s.MessageContainer>
              <s.SectionDescription
                variant="body2"
                color={
                  message.includes("sucesso") ? "success.main" : "error.main"
                }
              >
                {message}
              </s.SectionDescription>
            </s.MessageContainer>
          )}

          {/* Botão salvar */}
          <s.SaveButton
            variant="contained"
            color="primary"
            onClick={handleSave}
            loading={loading}
            loadingText="Salvando..."
          >
            Salvar Alterações
          </s.SaveButton>
        </s.MainContainer>
      </Container>
    </Fade>
  );
};

export default Profile;
